import { CombinedQueryReturn } from '/@src/models/api'
import { useRoute } from 'vue-router'
import { ref, watch } from 'vue'
import {
  AuthorKind,
  ContentListDocument,
  ContentListQuery,
  ContentListQueryVariables,
  ContentStatus,
  FilterOperator,
  RelationFilter,
  RelationKind,
  useContentListLazyQuery,
  useContentListQuery,
} from '/@src/__generated__/generated-gql-types'
import { ContentStatusValue } from '/@src/models/content'
import { toISOLocal } from '/@src/utils/date'
import { FetchPolicy } from '@apollo/client/core/watchQueryOptions'
import { ApolloQueryResult } from '@apollo/client'

export interface ContentListOptions {
  lazy?: boolean
  contentsKind?: RelationKind
  contentsGroup?: number
  fetchPolicy?: FetchPolicy
  nextFetchPolicy?: FetchPolicy
  authorKind?: AuthorKind
  skipRelations?: boolean
  specialKind?: RelationKind
}

export const useContentsList = (
  variables: ContentListQueryVariables,
  options?: ContentListOptions
) => {
  const route = useRoute()
  const page = ref(route.query.page ? parseInt(route.query.page as string) : 1)

  let queryResult: CombinedQueryReturn<
    ContentListQuery,
    ContentListQueryVariables
  >
  console.log('options', options)
  if (options?.lazy) {
    queryResult = useContentListLazyQuery(getQueryVariables(), {
      errorPolicy: 'all',
      fetchPolicy: options?.fetchPolicy,
      nextFetchPolicy: options?.nextFetchPolicy,
    })
  } else {
    queryResult = useContentListQuery(getQueryVariables(), {
      errorPolicy: 'all',
      fetchPolicy: options?.fetchPolicy || 'network-only',
      // nextFetchPolicy:options?.nextFetchPolicy||'cache-first'
    })
    console.log('result', queryResult)
  }
  watch(route, () => {
    queryResult.refetch(getQueryVariables())
  })

  const loadData = (
    variables?: ContentListQueryVariables
  ): Promise<ContentListQuery> | undefined => {
    const mergedVariables = {
      ...getQueryVariables(),
      ...variables,
    }
    return (
      queryResult.load?.(ContentListDocument, mergedVariables) ||
      queryResult.refetch(mergedVariables)?.then((result) => result.data)
    )
  }

  /**
   * 加载更多
   */
  const loadMore = (params: ContentListQueryVariables) => {
    if (!queryResult.result.value?.contents?.page_info?.has_next_page) {
      return
    }
    if (params) {
      params.after = queryResult.result.value?.contents?.page_info?.end_cursor
    }

    queryResult.fetchMore({
      variables: params,
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousResult
        return {
          contents: {
            __typename: fetchMoreResult.contents?.__typename,
            items: [
              ...(previousResult.contents?.items ?? []),
              ...fetchMoreResult.contents?.items!,
            ],
            page_info: fetchMoreResult?.contents?.page_info,
            total_count: fetchMoreResult.contents?.total_count || 0,
          },
        }
      },
    })
  }

  function getParmaStatus() {
    let status
    if (
      Object.values(ContentStatus)?.includes(
        (route.query.status as string)?.toUpperCase() as ContentStatus
      )
    ) {
      status = route.query.status as ContentStatus
      switch (route.query.status as ContentStatus) {
        case ContentStatus.Draft:
          status = ContentStatusValue.DRAFT
          break
        default:
          status = ContentStatusValue.PUBLISHED
          break
      }
    } else {
      status = undefined
    }
    return status
  }

  function getQueryVariables(): ContentListQueryVariables {
    let vip
    if (route.query.level) {
      vip = parseInt(route.query.level as string)
    }
    let p = 1
    if (route.query.page) {
      p = parseInt(route.query.page as string)
    }
    page.value = p

    let customRelations = variables.relations ?? undefined
    if (route.query.tagIds) {
      const tagIdsList = (route.query.tagIds as string).split(',')
      customRelations = tagIdsList.flatMap((x: string) => [
        {
          id: parseInt(x),
          kind: options?.contentsKind,
          groups: options?.contentsGroup,
        },
        ...(options?.specialKind
          ? [
              {
                id: parseInt(x),
                kind: options.specialKind,
                groups: options.contentsGroup,
              },
            ]
          : []),
      ]) as []
    }
    let relationOp = variables.ralationsOp ?? FilterOperator.Or
    // 筛选国家
    if (route.query.country) {
      const [country, extra] = (route.query.country as string).split('_')
      const countryRelation = {
        id: parseInt(extra),
        kind: RelationKind.Tag,
      }
      if (customRelations) {
        ;(customRelations as RelationFilter[]).push(countryRelation)
      } else {
        customRelations = [countryRelation]
      }
      relationOp = FilterOperator.And
    }
    // 排除项目
    if (route.query.excludeIds) {
      let kind = RelationKind.Tag
      if (route.query.excludeKind) {
        kind = route.query.excludeKind
      }
      relationOp = FilterOperator.And
      const excludeRelations = (route.query.excludeIds as string)
        .split(',')
        .map((it) => {
          return {
            id: parseInt(it),
            kind: kind,
            exclude: true,
          }
        })
      if (customRelations) {
        ;(customRelations as RelationFilter[]).push(...excludeRelations)
      } else {
        customRelations = excludeRelations
      }
    }
    // 筛选分类
    if (route.query.category) {
      const [category, extra] = (route.query.category as string).split('_')
      const cateRelation = {
        id: parseInt(extra),
        kind: RelationKind.Tag,
      }
      if (customRelations) {
        ;(customRelations as RelationFilter[]).push(cateRelation)
      } else {
        customRelations = [cateRelation]
      }
      relationOp = FilterOperator.And
    }

    let authorsList = variables.authors ?? undefined
    if (route.query.sourceIds) {
      authorsList = [
        {
          ids: (route.query.sourceIds as string)
            .split(',')
            .map((x) => parseInt(x)),
          kind: options?.authorKind,
        },
      ]
    }

    const sort = route.query.sort === 'desc' ? 'datetime:desc' : undefined
    const limit = variables.first || 20

    return {
      first: limit,
      kinds: variables.kinds,
      lite: variables.lite ?? true,
      status: getParmaStatus(),
      track: route.query.k as string,
      userId: route.query.userId
        ? parseInt(route.query.userId as string)
        : undefined,
      offset: (p - 1) * limit,
      vipVisibility: vip,
      relations: customRelations,
      after: variables.after,
      startTime: route.query.start
        ? toISOLocal(new Date(parseInt(route.query.start as string)))
        : variables.startTime,
      endTime: route.query.end
        ? toISOLocal(new Date(parseInt(route.query.end as string)))
        : variables.endTime,
      sortBy: sort || variables.sortBy,
      relatedContentsFirst: variables.relatedContentsFirst,
      relatedContentsKinds: variables.relatedContentsKinds,
      associateWithKinds: variables.associateWithKinds,
      authors: authorsList,
      ralationsOp: relationOp,
      associatedByKinds: variables.associatedByKinds,
    }
  }
  return {
    page,
    ...queryResult,
    loadData,
    loadMore,
  }
}
